import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { 
  Lock, LogOut, LayoutDashboard, Music, Calendar, Radio, 
  Mail, Users, Send, Video, Edit, Trash2, Plus, Save, X,
  ChevronDown, ChevronUp, Eye, EyeOff, Check, Clock, XCircle, User, Settings, Key, Tv,
  Upload, Play, Pause, RotateCw, FileAudio, HardDrive, Mic2, Star, Instagram, Globe, Camera, Image
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Password Input Component with Toggle Visibility
const PasswordInput = ({ value, onChange, placeholder, testId, required = true, label }) => {
  const [showPassword, setShowPassword] = useState(false);
  
  return (
    <div>
      {label && (
        <label className="font-mono text-xs tracking-widest text-chrome-mid block mb-2">
          {label}
        </label>
      )}
      <div className="relative">
        <input
          type={showPassword ? "text" : "password"}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          className="radio-input pr-12"
          required={required}
          data-testid={testId}
        />
        <button
          type="button"
          onClick={() => setShowPassword(!showPassword)}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-chrome-mid hover:text-white transition-colors p-1"
          data-testid={`${testId}-toggle`}
        >
          {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
        </button>
      </div>
    </div>
  );
};

// Admin Auth Context
const useAdminAuth = () => {
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminToken, setAdminToken] = useState(localStorage.getItem("hotlive95_admin_token"));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (adminToken) {
      verifyAdmin();
    } else {
      setLoading(false);
    }
  }, [adminToken]);

  const verifyAdmin = async () => {
    try {
      await axios.get(`${API}/admin/verify`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setIsAdmin(true);
    } catch (e) {
      localStorage.removeItem("hotlive95_admin_token");
      setAdminToken(null);
      setIsAdmin(false);
    } finally {
      setLoading(false);
    }
  };

  const login = async (username, password) => {
    const response = await axios.post(`${API}/admin/login`, { username, password });
    localStorage.setItem("hotlive95_admin_token", response.data.access_token);
    setAdminToken(response.data.access_token);
    setIsAdmin(true);
  };

  const logout = () => {
    localStorage.removeItem("hotlive95_admin_token");
    setAdminToken(null);
    setIsAdmin(false);
  };

  const setToken = (newToken) => {
    localStorage.setItem("hotlive95_admin_token", newToken);
    setAdminToken(newToken);
  };

  return { isAdmin, adminToken, loading, login, logout, setToken };
};

// Admin Login Component
const AdminLogin = ({ onLogin }) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await onLogin(username, password);
      toast.success("Welcome, Admin!");
    } catch (err) {
      toast.error("Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-surface border border-white/10 p-8 max-w-md w-full"
      >
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 bg-neon-red flex items-center justify-center">
            <Lock className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="font-display text-2xl chrome-text">ADMIN ACCESS</h1>
            <p className="font-mono text-xs text-neon-red">HOT LIVE 95 DETROIT</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="font-mono text-xs tracking-widest text-chrome-mid block mb-2">USERNAME</label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="radio-input"
              required
              data-testid="admin-username"
            />
          </div>
          <PasswordInput
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter password"
            testId="admin-password"
            label="PASSWORD"
          />
          <button 
            type="submit" 
            className="chrome-btn w-full"
            disabled={loading}
            data-testid="admin-login-btn"
          >
            {loading ? "Authenticating..." : "Login"}
          </button>
        </form>
      </motion.div>
    </div>
  );
};

// Stats Card Component
const StatsCard = ({ icon: Icon, label, value, subValue }) => (
  <div className="bg-surface border border-white/10 p-6">
    <div className="flex items-center gap-3 mb-2">
      <Icon className="w-5 h-5 text-neon-red" />
      <span className="font-mono text-xs tracking-widest text-chrome-mid">{label}</span>
    </div>
    <p className="font-display text-3xl text-white">{value}</p>
    {subValue && <p className="text-sm text-chrome-mid mt-1">{subValue}</p>}
  </div>
);

// Collapsible Section Component
const CollapsibleSection = ({ title, icon: Icon, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="bg-surface border border-white/10 mb-4">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full p-4 flex items-center justify-between hover:bg-white/5 transition-colors"
      >
        <div className="flex items-center gap-3">
          <Icon className="w-5 h-5 text-neon-red" />
          <span className="font-display text-lg">{title}</span>
        </div>
        {isOpen ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
      </button>
      {isOpen && <div className="p-4 border-t border-white/10">{children}</div>}
    </div>
  );
};

// Segments Management
const SegmentsManager = ({ token }) => {
  const [segments, setSegments] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [formData, setFormData] = useState({
    name: "", slug: "", description: "", genre: "Blues", 
    host: "", schedule: "", image_url: "", is_featured: false
  });

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { fetchSegments(); }, []);

  const fetchSegments = async () => {
    try {
      const res = await axios.get(`${API}/segments`);
      setSegments(res.data);
    } catch (e) { console.error(e); }
  };

  const handleSave = async () => {
    try {
      if (editingId) {
        await axios.put(`${API}/admin/segments/${editingId}`, formData, { headers });
        toast.success("Segment updated");
      } else {
        await axios.post(`${API}/admin/segments`, formData, { headers });
        toast.success("Segment created");
      }
      fetchSegments();
      resetForm();
    } catch (e) {
      toast.error("Failed to save segment");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this segment?")) return;
    try {
      await axios.delete(`${API}/admin/segments/${id}`, { headers });
      toast.success("Segment deleted");
      fetchSegments();
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  const resetForm = () => {
    setFormData({ name: "", slug: "", description: "", genre: "Blues", host: "", schedule: "", image_url: "", is_featured: false });
    setEditingId(null);
    setShowAdd(false);
  };

  const startEdit = (segment) => {
    setFormData(segment);
    setEditingId(segment.id);
    setShowAdd(true);
  };

  const genres = ["Blues", "R&B", "Jazz", "Gospel", "Rock", "Soft Rock", "Neo Soul", "Techno"];

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <span className="text-chrome-mid">{segments.length} segments</span>
        <button onClick={() => setShowAdd(true)} className="primary-btn text-xs flex items-center gap-2">
          <Plus className="w-4 h-4" /> Add Segment
        </button>
      </div>

      {showAdd && (
        <div className="bg-black/50 border border-neon-red/30 p-4 mb-4 space-y-3">
          <div className="grid md:grid-cols-2 gap-3">
            <input placeholder="Name" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} className="radio-input text-sm" />
            <input placeholder="Slug (url-friendly)" value={formData.slug} onChange={(e) => setFormData({...formData, slug: e.target.value})} className="radio-input text-sm" />
            <input placeholder="Host" value={formData.host} onChange={(e) => setFormData({...formData, host: e.target.value})} className="radio-input text-sm" />
            <input placeholder="Schedule (e.g., Mon-Fri 6PM)" value={formData.schedule} onChange={(e) => setFormData({...formData, schedule: e.target.value})} className="radio-input text-sm" />
            <select value={formData.genre} onChange={(e) => setFormData({...formData, genre: e.target.value})} className="radio-input text-sm">
              {genres.map(g => <option key={g} value={g}>{g}</option>)}
            </select>
            <input placeholder="Image URL" value={formData.image_url} onChange={(e) => setFormData({...formData, image_url: e.target.value})} className="radio-input text-sm" />
          </div>
          <textarea placeholder="Description" value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} className="radio-input text-sm w-full" rows={2} />
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={formData.is_featured} onChange={(e) => setFormData({...formData, is_featured: e.target.checked})} className="accent-neon-red" />
            Featured Segment
          </label>
          <div className="flex gap-2">
            <button onClick={handleSave} className="chrome-btn text-xs flex items-center gap-2"><Save className="w-4 h-4" /> Save</button>
            <button onClick={resetForm} className="outline-btn text-xs flex items-center gap-2"><X className="w-4 h-4" /> Cancel</button>
          </div>
        </div>
      )}

      <div className="space-y-2 max-h-96 overflow-y-auto">
        {segments.map(seg => (
          <div key={seg.id} className="flex items-center justify-between p-3 bg-black/30 border border-white/5">
            <div className="flex items-center gap-3">
              {seg.image_url && <img src={seg.image_url} alt="" className="w-10 h-10 object-cover" />}
              <div>
                <p className="font-medium">{seg.name}</p>
                <p className="text-xs text-chrome-mid">{seg.genre} • {seg.host}</p>
              </div>
              {seg.is_featured && <span className="text-xs bg-neon-red/20 text-neon-red px-2 py-0.5">Featured</span>}
            </div>
            <div className="flex gap-2">
              <button onClick={() => startEdit(seg)} className="p-2 hover:bg-white/10"><Edit className="w-4 h-4" /></button>
              <button onClick={() => handleDelete(seg.id)} className="p-2 hover:bg-red-500/20 text-red-500"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Schedule Management
const ScheduleManager = ({ token }) => {
  const [schedules, setSchedules] = useState([]);
  const [segments, setSegments] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    segment_id: "", segment_name: "", day_of_week: "Monday",
    start_time: "18:00", end_time: "20:00", host: "", description: ""
  });

  const headers = { Authorization: `Bearer ${token}` };
  const days = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

  useEffect(() => { 
    fetchSchedules(); 
    fetchSegments();
  }, []);

  const fetchSchedules = async () => {
    try {
      const res = await axios.get(`${API}/schedule`);
      setSchedules(res.data);
    } catch (e) { console.error(e); }
  };

  const fetchSegments = async () => {
    try {
      const res = await axios.get(`${API}/segments`);
      setSegments(res.data);
    } catch (e) { console.error(e); }
  };

  const handleSegmentSelect = (segmentId) => {
    const segment = segments.find(s => s.id === segmentId);
    if (segment) {
      setFormData({
        ...formData,
        segment_id: segment.id,
        segment_name: segment.name,
        host: segment.host
      });
    }
  };

  const handleSave = async () => {
    try {
      if (editingId) {
        await axios.put(`${API}/admin/schedule/${editingId}`, formData, { headers });
        toast.success("Schedule updated");
      } else {
        await axios.post(`${API}/admin/schedule`, formData, { headers });
        toast.success("Schedule created");
      }
      fetchSchedules();
      resetForm();
    } catch (e) {
      toast.error("Failed to save schedule");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this schedule entry?")) return;
    try {
      await axios.delete(`${API}/admin/schedule/${id}`, { headers });
      toast.success("Schedule deleted");
      fetchSchedules();
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  const resetForm = () => {
    setFormData({ segment_id: "", segment_name: "", day_of_week: "Monday", start_time: "18:00", end_time: "20:00", host: "", description: "" });
    setEditingId(null);
    setShowAdd(false);
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <span className="text-chrome-mid">{schedules.length} scheduled shows</span>
        <button onClick={() => setShowAdd(true)} className="primary-btn text-xs flex items-center gap-2">
          <Plus className="w-4 h-4" /> Add Schedule
        </button>
      </div>

      {showAdd && (
        <div className="bg-black/50 border border-neon-red/30 p-4 mb-4 space-y-3">
          <div className="grid md:grid-cols-2 gap-3">
            <select value={formData.segment_id} onChange={(e) => handleSegmentSelect(e.target.value)} className="radio-input text-sm">
              <option value="">Select Segment</option>
              {segments.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            <select value={formData.day_of_week} onChange={(e) => setFormData({...formData, day_of_week: e.target.value})} className="radio-input text-sm">
              {days.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
            <input type="time" value={formData.start_time} onChange={(e) => setFormData({...formData, start_time: e.target.value})} className="radio-input text-sm" />
            <input type="time" value={formData.end_time} onChange={(e) => setFormData({...formData, end_time: e.target.value})} className="radio-input text-sm" />
            <input placeholder="Host" value={formData.host} onChange={(e) => setFormData({...formData, host: e.target.value})} className="radio-input text-sm" />
            <input placeholder="Description" value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} className="radio-input text-sm" />
          </div>
          <div className="flex gap-2">
            <button onClick={handleSave} className="chrome-btn text-xs flex items-center gap-2"><Save className="w-4 h-4" /> Save</button>
            <button onClick={resetForm} className="outline-btn text-xs flex items-center gap-2"><X className="w-4 h-4" /> Cancel</button>
          </div>
        </div>
      )}

      <div className="space-y-2 max-h-96 overflow-y-auto">
        {days.map(day => {
          const daySchedules = schedules.filter(s => s.day_of_week === day);
          if (daySchedules.length === 0) return null;
          return (
            <div key={day} className="mb-3">
              <p className="font-mono text-xs text-neon-red mb-2">{day.toUpperCase()}</p>
              {daySchedules.map(sch => (
                <div key={sch.id} className="flex items-center justify-between p-3 bg-black/30 border border-white/5 mb-1">
                  <div>
                    <p className="font-medium">{sch.segment_name}</p>
                    <p className="text-xs text-chrome-mid">{sch.start_time} - {sch.end_time} • {sch.host}</p>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => { setFormData(sch); setEditingId(sch.id); setShowAdd(true); }} className="p-2 hover:bg-white/10"><Edit className="w-4 h-4" /></button>
                    <button onClick={() => handleDelete(sch.id)} className="p-2 hover:bg-red-500/20 text-red-500"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Song Requests Manager
const RequestsManager = ({ token }) => {
  const [requests, setRequests] = useState([]);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { fetchRequests(); }, []);

  const fetchRequests = async () => {
    try {
      const res = await axios.get(`${API}/requests`);
      setRequests(res.data);
    } catch (e) { console.error(e); }
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.put(`${API}/admin/requests/${id}/status?new_status=${status}`, {}, { headers });
      toast.success(`Marked as ${status}`);
      fetchRequests();
    } catch (e) {
      toast.error("Failed to update");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this request?")) return;
    try {
      await axios.delete(`${API}/admin/requests/${id}`, { headers });
      toast.success("Request deleted");
      fetchRequests();
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  const statusColors = {
    pending: "bg-yellow-500/20 text-yellow-500",
    played: "bg-green-500/20 text-green-500",
    rejected: "bg-red-500/20 text-red-500"
  };

  return (
    <div className="space-y-2 max-h-96 overflow-y-auto">
      {requests.length === 0 ? (
        <p className="text-chrome-mid text-center py-4">No song requests yet</p>
      ) : (
        requests.map(req => (
          <div key={req.id} className="p-3 bg-black/30 border border-white/5">
            <div className="flex justify-between items-start mb-2">
              <div>
                <p className="font-medium">{req.song_title} - {req.artist}</p>
                <p className="text-xs text-chrome-mid">{req.user_name} • {req.genre}</p>
                {req.message && <p className="text-xs text-chrome-mid mt-1 italic">"{req.message}"</p>}
              </div>
              <span className={`text-xs px-2 py-0.5 ${statusColors[req.status]}`}>{req.status}</span>
            </div>
            <div className="flex gap-2">
              <button onClick={() => updateStatus(req.id, "played")} className="text-xs flex items-center gap-1 px-2 py-1 bg-green-500/10 hover:bg-green-500/20 text-green-500"><Check className="w-3 h-3" /> Played</button>
              <button onClick={() => updateStatus(req.id, "rejected")} className="text-xs flex items-center gap-1 px-2 py-1 bg-red-500/10 hover:bg-red-500/20 text-red-500"><XCircle className="w-3 h-3" /> Reject</button>
              <button onClick={() => handleDelete(req.id)} className="text-xs flex items-center gap-1 px-2 py-1 bg-white/5 hover:bg-white/10"><Trash2 className="w-3 h-3" /> Delete</button>
            </div>
          </div>
        ))
      )}
    </div>
  );
};

// Submissions Manager with Audio Preview + Rotation
const SubmissionsManager = ({ token }) => {
  const [submissions, setSubmissions] = useState([]);
  const [filter, setFilter] = useState("all");
  const [playingId, setPlayingId] = useState(null);
  const [showRotationModal, setShowRotationModal] = useState(null);
  const [rotationForm, setRotationForm] = useState({ day_of_week: "Monday", time_slot: "12:00", priority: "normal" });
  const audioRef = useRef(null);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { fetchSubmissions(); }, []);

  const fetchSubmissions = async () => {
    try {
      const res = await axios.get(`${API}/submissions`);
      setSubmissions(res.data);
    } catch (e) { console.error(e); }
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.put(`${API}/admin/submissions/${id}/status?new_status=${status}`, {}, { headers });
      toast.success(`Status updated to ${status}`);
      fetchSubmissions();
    } catch (e) {
      toast.error("Failed to update");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this submission?")) return;
    try {
      await axios.delete(`${API}/admin/submissions/${id}`, { headers });
      toast.success("Submission deleted");
      fetchSubmissions();
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  const togglePlay = (sub) => {
    if (!sub.media_url || (!sub.media_url.startsWith("/api/") && !sub.media_url.startsWith("http"))) {
      toast.error("No playable file for this submission");
      return;
    }
    if (playingId === sub.id) {
      audioRef.current?.pause();
      setPlayingId(null);
    } else {
      if (audioRef.current) {
        const url = sub.media_url.startsWith("/api/") ? `${BACKEND_URL}${sub.media_url}` : sub.media_url;
        audioRef.current.src = url;
        audioRef.current.play().catch(() => toast.error("Cannot play this file"));
        setPlayingId(sub.id);
      }
    }
  };

  const addToRotation = async (submissionId) => {
    try {
      await axios.post(`${API}/admin/rotation`, {
        submission_id: submissionId,
        ...rotationForm
      }, { headers });
      toast.success("Added to rotation!");
      setShowRotationModal(null);
      fetchSubmissions();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add to rotation");
    }
  };

  const statusColors = {
    pending: "bg-yellow-500/20 text-yellow-500",
    under_review: "bg-blue-500/20 text-blue-500",
    approved: "bg-green-500/20 text-green-500",
    rejected: "bg-red-500/20 text-red-500"
  };

  const days = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
  const filteredSubs = filter === "all" ? submissions : submissions.filter(s => s.status === filter);

  return (
    <div>
      {/* Hidden Audio Element */}
      <audio ref={audioRef} onEnded={() => setPlayingId(null)} />
      
      {/* Filter Tabs */}
      <div className="flex gap-2 mb-4 flex-wrap">
        {["all", "pending", "under_review", "approved", "rejected"].map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`text-xs px-3 py-1.5 transition-colors ${
              filter === f ? "bg-neon-red text-white" : "bg-black/30 text-chrome-mid hover:text-white"
            }`}
            data-testid={`filter-${f}`}
          >
            {f === "all" ? `All (${submissions.length})` : `${f.replace("_", " ")} (${submissions.filter(s => s.status === f).length})`}
          </button>
        ))}
      </div>

      <div className="space-y-3 max-h-[500px] overflow-y-auto">
        {filteredSubs.length === 0 ? (
          <p className="text-chrome-mid text-center py-4">No submissions found</p>
        ) : (
          filteredSubs.map(sub => (
            <div key={sub.id} className="p-4 bg-black/30 border border-white/5" data-testid={`submission-${sub.id}`}>
              <div className="flex justify-between items-start mb-3">
                <div className="flex items-start gap-3">
                  {/* Play Button for uploaded files */}
                  {sub.media_url && sub.media_url.startsWith("/api/") && (
                    <button
                      onClick={() => togglePlay(sub)}
                      className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${
                        playingId === sub.id ? "bg-neon-red" : "bg-white/10 hover:bg-white/20"
                      }`}
                      data-testid={`play-${sub.id}`}
                    >
                      {playingId === sub.id ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
                    </button>
                  )}
                  <div>
                    <div className="flex items-center gap-2">
                      {sub.submission_type === "song" ? <Music className="w-4 h-4 text-neon-red" /> : <Video className="w-4 h-4 text-neon-red" />}
                      <p className="font-medium">{sub.track_title}</p>
                    </div>
                    <p className="text-xs text-chrome-mid">{sub.artist_name} &bull; {sub.genre} &bull; {sub.artist_email}</p>
                    {sub.media_url && sub.media_url.startsWith("/api/") && (
                      <span className="text-xs bg-green-500/10 text-green-400 px-2 py-0.5 mt-1 inline-flex items-center gap-1">
                        <FileAudio className="w-3 h-3" /> File uploaded
                      </span>
                    )}
                    {sub.media_url && !sub.media_url.startsWith("/api/") && (
                      <a href={sub.media_url} target="_blank" rel="noopener noreferrer" className="text-xs text-neon-red hover:underline flex items-center gap-1 mt-1">
                        <Eye className="w-3 h-3" /> External link
                      </a>
                    )}
                    {sub.rotation_status === "in_rotation" && (
                      <span className="text-xs bg-blue-500/10 text-blue-400 px-2 py-0.5 mt-1 inline-flex items-center gap-1 ml-1">
                        <RotateCw className="w-3 h-3" /> In rotation
                      </span>
                    )}
                  </div>
                </div>
                <span className={`text-xs px-2 py-0.5 flex-shrink-0 ${statusColors[sub.status]}`}>{sub.status}</span>
              </div>
              
              <div className="flex flex-wrap gap-2">
                <button onClick={() => updateStatus(sub.id, "under_review")} className="text-xs flex items-center gap-1 px-2 py-1 bg-blue-500/10 hover:bg-blue-500/20 text-blue-500" data-testid={`review-${sub.id}`}><Clock className="w-3 h-3" /> Review</button>
                <button onClick={() => updateStatus(sub.id, "approved")} className="text-xs flex items-center gap-1 px-2 py-1 bg-green-500/10 hover:bg-green-500/20 text-green-500" data-testid={`approve-${sub.id}`}><Check className="w-3 h-3" /> Approve</button>
                <button onClick={() => updateStatus(sub.id, "rejected")} className="text-xs flex items-center gap-1 px-2 py-1 bg-red-500/10 hover:bg-red-500/20 text-red-500"><XCircle className="w-3 h-3" /> Reject</button>
                <button onClick={() => { setShowRotationModal(sub.id); setRotationForm({ day_of_week: "Monday", time_slot: "12:00", priority: "normal" }); }} className="text-xs flex items-center gap-1 px-2 py-1 bg-purple-500/10 hover:bg-purple-500/20 text-purple-400" data-testid={`schedule-${sub.id}`}><RotateCw className="w-3 h-3" /> Add to Rotation</button>
                <button onClick={() => handleDelete(sub.id)} className="text-xs flex items-center gap-1 px-2 py-1 bg-white/5 hover:bg-white/10"><Trash2 className="w-3 h-3" /> Delete</button>
              </div>

              {/* Rotation Scheduling Modal */}
              {showRotationModal === sub.id && (
                <div className="mt-3 p-3 bg-purple-500/5 border border-purple-500/20 rounded">
                  <p className="text-xs text-purple-400 mb-2 font-medium">SCHEDULE IN ROTATION</p>
                  <div className="grid grid-cols-3 gap-2">
                    <select value={rotationForm.day_of_week} onChange={(e) => setRotationForm({...rotationForm, day_of_week: e.target.value})} className="radio-input text-xs">
                      {days.map(d => <option key={d} value={d}>{d}</option>)}
                    </select>
                    <input type="time" value={rotationForm.time_slot} onChange={(e) => setRotationForm({...rotationForm, time_slot: e.target.value})} className="radio-input text-xs" />
                    <select value={rotationForm.priority} onChange={(e) => setRotationForm({...rotationForm, priority: e.target.value})} className="radio-input text-xs">
                      <option value="high">High</option>
                      <option value="normal">Normal</option>
                      <option value="low">Low</option>
                    </select>
                  </div>
                  <div className="flex gap-2 mt-2">
                    <button onClick={() => addToRotation(sub.id)} className="text-xs px-3 py-1 bg-purple-500 text-white hover:bg-purple-600">Schedule</button>
                    <button onClick={() => setShowRotationModal(null)} className="text-xs px-3 py-1 bg-white/5 text-chrome-mid">Cancel</button>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

// DJ Profile Manager
const DJManager = ({ token }) => {
  const [djs, setDjs] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({
    name: "", stage_name: "", bio: "", genres: [], show_name: "", show_schedule: "",
    instagram: "", twitter: "", facebook: "", soundcloud: "", website: "", is_featured: false
  });
  const headers = { Authorization: `Bearer ${token}` };
  const photoRef = useRef(null);
  const allGenres = ["Blues","R&B","Jazz","Soul","Gospel","Rock","Hip-Hop","Techno","Neo Soul","Electronic","Soft Rock","Other"];

  useEffect(() => { fetchDJs(); }, []);

  const fetchDJs = async () => {
    try { const r = await axios.get(`${API}/djs?active_only=false`); setDjs(r.data); } catch (e) {}
  };

  const resetForm = () => {
    setForm({ name: "", stage_name: "", bio: "", genres: [], show_name: "", show_schedule: "", instagram: "", twitter: "", facebook: "", soundcloud: "", website: "", is_featured: false });
    setEditingId(null);
    setShowForm(false);
  };

  const handleSubmit = async () => {
    if (!form.name || !form.bio) { toast.error("Name and bio are required"); return; }
    try {
      if (editingId) {
        await axios.put(`${API}/admin/djs/${editingId}`, form, { headers });
        toast.success("DJ profile updated");
      } else {
        await axios.post(`${API}/admin/djs`, { ...form, is_active: true }, { headers });
        toast.success("DJ profile created");
      }
      resetForm();
      fetchDJs();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    }
  };

  const handleEdit = (dj) => {
    setForm({
      name: dj.name || "", stage_name: dj.stage_name || "", bio: dj.bio || "",
      genres: dj.genres || [], show_name: dj.show_name || "", show_schedule: dj.show_schedule || "",
      instagram: dj.instagram || "", twitter: dj.twitter || "", facebook: dj.facebook || "",
      soundcloud: dj.soundcloud || "", website: dj.website || "", is_featured: dj.is_featured || false
    });
    setEditingId(dj.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this DJ profile?")) return;
    try { await axios.delete(`${API}/admin/djs/${id}`, { headers }); toast.success("DJ deleted"); fetchDJs(); } catch (e) { toast.error("Failed to delete"); }
  };

  const handlePhotoUpload = async (djId, file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await axios.post(`${API}/admin/djs/${djId}/photo`, fd, { headers: { ...headers, "Content-Type": "multipart/form-data" } });
      toast.success("Photo uploaded");
      fetchDJs();
    } catch (e) {
      toast.error("Photo upload failed");
    } finally { setUploading(false); }
  };

  const toggleGenre = (g) => {
    setForm(prev => ({
      ...prev,
      genres: prev.genres.includes(g) ? prev.genres.filter(x => x !== g) : [...prev.genres, g]
    }));
  };

  const [expandedGallery, setExpandedGallery] = useState(null);
  const [galleryPhotos, setGalleryPhotos] = useState({});

  const fetchGallery = async (djId) => {
    try {
      const res = await axios.get(`${API}/djs/${djId}/gallery`);
      setGalleryPhotos(prev => ({ ...prev, [djId]: res.data }));
    } catch (e) {}
  };

  const handleGalleryUpload = async (djId, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("caption", "");
    try {
      await axios.post(`${API}/admin/djs/${djId}/gallery`, fd, { headers: { ...headers, "Content-Type": "multipart/form-data" } });
      toast.success("Gallery photo added");
      fetchGallery(djId);
    } catch (e) {
      toast.error("Gallery upload failed");
    }
  };

  const handleGalleryDelete = async (djId, photoId) => {
    try {
      await axios.delete(`${API}/admin/djs/${djId}/gallery/${photoId}`, { headers });
      toast.success("Photo removed");
      fetchGallery(djId);
    } catch (e) {
      toast.error("Failed to delete photo");
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <p className="text-chrome-mid">{djs.length} DJ(s)</p>
        <button onClick={() => { resetForm(); setShowForm(!showForm); }} className="text-xs flex items-center gap-1 px-3 py-1.5 bg-neon-red text-white hover:bg-red-700" data-testid="add-dj-btn">
          <Plus className="w-3 h-3" /> {showForm ? "Cancel" : "Add DJ"}
        </button>
      </div>

      {showForm && (
        <div className="mb-6 p-4 bg-black/30 border border-white/10 space-y-3">
          <p className="text-xs text-neon-red font-medium mb-2">{editingId ? "EDIT DJ" : "NEW DJ"}</p>
          <div className="grid grid-cols-2 gap-3">
            <input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="radio-input text-xs" placeholder="Full Name *" data-testid="dj-form-name" />
            <input value={form.stage_name} onChange={(e) => setForm({...form, stage_name: e.target.value})} className="radio-input text-xs" placeholder="Stage Name / DJ Name" data-testid="dj-form-stage-name" />
            <input value={form.show_name} onChange={(e) => setForm({...form, show_name: e.target.value})} className="radio-input text-xs" placeholder="Show Name" />
            <input value={form.show_schedule} onChange={(e) => setForm({...form, show_schedule: e.target.value})} className="radio-input text-xs" placeholder="Schedule (e.g. Mon-Fri 6PM-10PM)" />
          </div>
          <textarea value={form.bio} onChange={(e) => setForm({...form, bio: e.target.value})} className="radio-input text-xs w-full min-h-[60px] resize-none" placeholder="Bio *" data-testid="dj-form-bio" />
          
          <div><p className="text-xs text-chrome-dark mb-1">Genres</p><div className="flex flex-wrap gap-1">{allGenres.map(g => (<button key={g} type="button" onClick={() => toggleGenre(g)} className={`text-[10px] px-2 py-0.5 transition-colors ${form.genres.includes(g) ? "bg-neon-red text-white" : "bg-white/5 text-chrome-mid hover:bg-white/10"}`}>{g}</button>))}</div></div>

          <div className="grid grid-cols-2 gap-3">
            <input value={form.instagram} onChange={(e) => setForm({...form, instagram: e.target.value})} className="radio-input text-xs" placeholder="Instagram @handle" />
            <input value={form.twitter} onChange={(e) => setForm({...form, twitter: e.target.value})} className="radio-input text-xs" placeholder="Twitter @handle" />
            <input value={form.facebook} onChange={(e) => setForm({...form, facebook: e.target.value})} className="radio-input text-xs" placeholder="Facebook URL" />
            <input value={form.website} onChange={(e) => setForm({...form, website: e.target.value})} className="radio-input text-xs" placeholder="Website URL" />
          </div>

          <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={form.is_featured} onChange={(e) => setForm({...form, is_featured: e.target.checked})} className="accent-red-500" /><span className="text-xs text-chrome-mid flex items-center gap-1"><Star className="w-3 h-3" /> Featured DJ</span></label>

          <button onClick={handleSubmit} className="text-xs px-4 py-2 bg-neon-red text-white hover:bg-red-700 flex items-center gap-1" data-testid="save-dj-btn">
            <Save className="w-3 h-3" /> {editingId ? "Update" : "Create"} DJ Profile
          </button>
        </div>
      )}

      <div className="space-y-3 max-h-[500px] overflow-y-auto">
        {djs.map(dj => {
          const photoUrl = dj.photo_url ? (dj.photo_url.startsWith("/api/") ? `${BACKEND_URL}${dj.photo_url}` : dj.photo_url) : null;
          const isGalleryOpen = expandedGallery === dj.id;
          const photos = galleryPhotos[dj.id] || [];
          return (
            <div key={dj.id} className="bg-black/30 border border-white/5" data-testid={`admin-dj-${dj.id}`}>
              <div className="p-3 flex items-center gap-4">
                <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0 bg-surface-highlight flex items-center justify-center cursor-pointer relative" onClick={() => { const input = document.createElement('input'); input.type = 'file'; input.accept = 'image/*'; input.onchange = (e) => handlePhotoUpload(dj.id, e.target.files[0]); input.click(); }}>
                  {photoUrl ? <img src={photoUrl} alt={dj.name} className="w-full h-full object-cover" /> : <Mic2 className="w-6 h-6 text-chrome-dark" />}
                  {uploading && <div className="absolute inset-0 bg-black/50 flex items-center justify-center"><div className="w-4 h-4 border-2 border-neon-red border-t-transparent rounded-full animate-spin" /></div>}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-sm truncate">{dj.stage_name || dj.name}</p>
                    {dj.is_featured && <Star className="w-3 h-3 text-neon-red fill-neon-red flex-shrink-0" />}
                    {!dj.is_active && <span className="text-[10px] bg-yellow-500/20 text-yellow-500 px-1.5">inactive</span>}
                  </div>
                  <p className="text-xs text-chrome-mid truncate">{dj.show_name || "No show"} {dj.show_schedule ? `• ${dj.show_schedule}` : ""}</p>
                  <div className="flex gap-1 mt-1">{(dj.genres || []).slice(0, 3).map(g => <span key={g} className="text-[9px] bg-white/5 text-chrome-dark px-1.5 py-0.5">{g}</span>)}</div>
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  <button onClick={() => { if (isGalleryOpen) { setExpandedGallery(null); } else { setExpandedGallery(dj.id); fetchGallery(dj.id); } }} className={`p-1.5 hover:bg-white/10 ${isGalleryOpen ? 'text-neon-red' : 'text-chrome-mid'}`} title="Gallery" data-testid={`gallery-toggle-${dj.id}`}><Camera className="w-3 h-3" /></button>
                  <button onClick={() => handleEdit(dj)} className="p-1.5 hover:bg-white/10" title="Edit"><Edit className="w-3 h-3 text-chrome-mid" /></button>
                  <button onClick={() => handleDelete(dj.id)} className="p-1.5 hover:bg-red-500/20" title="Delete"><Trash2 className="w-3 h-3 text-red-400" /></button>
                </div>
              </div>

              {/* Gallery Section */}
              {isGalleryOpen && (
                <div className="px-3 pb-3 border-t border-white/5 pt-3">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs text-chrome-mid flex items-center gap-1"><Image className="w-3 h-3" /> {photos.length} gallery photo(s)</p>
                    <button onClick={() => { const input = document.createElement('input'); input.type = 'file'; input.accept = 'image/*'; input.multiple = true; input.onchange = async (e) => { for (const f of e.target.files) { await handleGalleryUpload(dj.id, f); } }; input.click(); }} className="text-[10px] flex items-center gap-1 px-2 py-1 bg-neon-red/20 text-neon-red hover:bg-neon-red/30" data-testid={`add-gallery-photo-${dj.id}`}>
                      <Plus className="w-3 h-3" /> Add Photos
                    </button>
                  </div>
                  {photos.length > 0 ? (
                    <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                      {photos.map(photo => {
                        const imgUrl = photo.photo_url.startsWith("/api/") ? `${BACKEND_URL}${photo.photo_url}` : photo.photo_url;
                        return (
                          <div key={photo.id} className="relative group aspect-square bg-surface-highlight overflow-hidden">
                            <img src={imgUrl} alt={photo.caption || "Gallery"} className="w-full h-full object-cover" />
                            <button onClick={() => handleGalleryDelete(dj.id, photo.id)} className="absolute top-1 right-1 p-0.5 bg-black/60 text-red-400 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-500 hover:text-white"><X className="w-3 h-3" /></button>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-xs text-chrome-dark text-center py-4">No gallery photos yet. Click "Add Photos" to upload performance photos.</p>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Rotation Schedule Manager
const RotationManager = ({ token }) => {
  const [slots, setSlots] = useState([]);
  const headers = { Authorization: `Bearer ${token}` };
  const days = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

  useEffect(() => { fetchRotation(); }, []);

  const fetchRotation = async () => {
    try {
      const res = await axios.get(`${API}/admin/rotation`, { headers });
      setSlots(res.data);
    } catch (e) { console.error(e); }
  };

  const updateSlotStatus = async (id, status) => {
    try {
      await axios.put(`${API}/admin/rotation/${id}/status?new_status=${status}`, {}, { headers });
      toast.success(`Slot marked as ${status}`);
      fetchRotation();
    } catch (e) {
      toast.error("Failed to update");
    }
  };

  const removeSlot = async (id) => {
    if (!window.confirm("Remove from rotation?")) return;
    try {
      await axios.delete(`${API}/admin/rotation/${id}`, { headers });
      toast.success("Removed from rotation");
      fetchRotation();
    } catch (e) {
      toast.error("Failed to remove");
    }
  };

  const priorityColors = {
    high: "text-red-400 bg-red-500/10",
    normal: "text-blue-400 bg-blue-500/10",
    low: "text-chrome-mid bg-white/5"
  };

  const statusColors = {
    scheduled: "bg-green-500/20 text-green-500",
    played: "bg-blue-500/20 text-blue-400",
    skipped: "bg-yellow-500/20 text-yellow-500"
  };

  return (
    <div>
      <p className="text-chrome-mid mb-4">{slots.length} tracks in rotation</p>
      
      {slots.length === 0 ? (
        <p className="text-chrome-mid text-center py-8">No tracks in rotation. Approve submissions and add them to rotation from the Submissions section.</p>
      ) : (
        <div className="space-y-4">
          {days.map(day => {
            const daySlots = slots.filter(s => s.day_of_week === day);
            if (daySlots.length === 0) return null;
            return (
              <div key={day}>
                <p className="font-mono text-xs text-neon-red mb-2 tracking-widest">{day.toUpperCase()}</p>
                <div className="space-y-1">
                  {daySlots.sort((a, b) => a.time_slot.localeCompare(b.time_slot)).map(slot => (
                    <div key={slot.id} className="flex items-center justify-between p-3 bg-black/30 border border-white/5" data-testid={`rotation-${slot.id}`}>
                      <div className="flex items-center gap-3">
                        <span className="font-mono text-xs text-chrome-mid w-12">{slot.time_slot}</span>
                        <div>
                          <p className="text-sm font-medium">{slot.track_title}</p>
                          <p className="text-xs text-chrome-mid">{slot.artist_name} &bull; {slot.genre}</p>
                        </div>
                        <span className={`text-xs px-2 py-0.5 ${priorityColors[slot.priority]}`}>{slot.priority}</span>
                        <span className={`text-xs px-2 py-0.5 ${statusColors[slot.status]}`}>{slot.status}</span>
                      </div>
                      <div className="flex gap-1">
                        <button onClick={() => updateSlotStatus(slot.id, "played")} className="p-1.5 hover:bg-green-500/20 text-green-500" title="Mark played"><Check className="w-3 h-3" /></button>
                        <button onClick={() => updateSlotStatus(slot.id, "skipped")} className="p-1.5 hover:bg-yellow-500/20 text-yellow-500" title="Mark skipped"><Clock className="w-3 h-3" /></button>
                        <button onClick={() => removeSlot(slot.id)} className="p-1.5 hover:bg-red-500/20 text-red-500" title="Remove"><Trash2 className="w-3 h-3" /></button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Podcast Stream Manager
const PodcastStreamManager = ({ token }) => {
  const [config, setConfig] = useState({
    youtube_url: "",
    is_live: false,
    show_title: "",
    show_description: "",
    host_name: "",
    next_show_time: ""
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { fetchConfig(); }, []);

  const fetchConfig = async () => {
    try {
      const res = await axios.get(`${API}/podcast/stream-config`);
      setConfig(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/podcast/stream-config`, config, { headers });
      toast.success("Stream configuration updated!");
    } catch (e) {
      toast.error("Failed to update configuration");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-center py-8 text-chrome-mid">Loading...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="bg-black/30 border border-neon-red/20 p-4">
        <h4 className="font-display text-sm mb-3 flex items-center gap-2">
          <Video className="w-4 h-4 text-neon-red" />
          YOUTUBE LIVE STREAM
        </h4>
        <input 
          placeholder="YouTube Live URL (e.g., https://www.youtube.com/watch?v=XXXXX)" 
          value={config.youtube_url} 
          onChange={(e) => setConfig({...config, youtube_url: e.target.value})} 
          className="radio-input text-sm w-full mb-3" 
        />
        <p className="text-xs text-chrome-mid mb-3">
          Paste your YouTube Live stream URL. This can be a regular YouTube URL or embed URL.
        </p>
        <label className="flex items-center gap-3 cursor-pointer">
          <input 
            type="checkbox" 
            checked={config.is_live} 
            onChange={(e) => setConfig({...config, is_live: e.target.checked})} 
            className="accent-neon-red w-5 h-5" 
          />
          <span className="text-sm">Stream is currently LIVE</span>
          {config.is_live && <span className="text-xs bg-neon-red px-2 py-0.5 animate-pulse">LIVE</span>}
        </label>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="space-y-3">
          <input 
            placeholder="Show Title" 
            value={config.show_title} 
            onChange={(e) => setConfig({...config, show_title: e.target.value})} 
            className="radio-input text-sm w-full" 
          />
          <input 
            placeholder="Host Name" 
            value={config.host_name} 
            onChange={(e) => setConfig({...config, host_name: e.target.value})} 
            className="radio-input text-sm w-full" 
          />
        </div>
        <div className="space-y-3">
          <input 
            placeholder="Next Show Time (e.g., Friday 8PM EST)" 
            value={config.next_show_time} 
            onChange={(e) => setConfig({...config, next_show_time: e.target.value})} 
            className="radio-input text-sm w-full" 
          />
          <textarea 
            placeholder="Show Description" 
            value={config.show_description} 
            onChange={(e) => setConfig({...config, show_description: e.target.value})} 
            className="radio-input text-sm w-full" 
            rows={2}
          />
        </div>
      </div>

      <button 
        onClick={handleSave} 
        disabled={saving}
        className="chrome-btn text-xs flex items-center gap-2 w-full justify-center"
      >
        <Save className="w-4 h-4" />
        {saving ? "Saving..." : "Save Stream Configuration"}
      </button>

      <div className="text-xs text-chrome-dark border-t border-white/10 pt-3">
        <p className="mb-1"><strong>How to stream:</strong></p>
        <ol className="list-decimal list-inside space-y-1">
          <li>Start a YouTube Live stream from your YouTube Studio</li>
          <li>Copy your YouTube Live URL</li>
          <li>Paste it above and check "Stream is currently LIVE"</li>
          <li>Click Save - Subscribers can now watch on your site!</li>
        </ol>
      </div>
    </div>
  );
};

// Now Playing Manager
const NowPlayingManager = ({ token }) => {
  const [formData, setFormData] = useState({
    track_title: "", artist: "", album: "", genre: "Blues", duration: 240, artwork_url: ""
  });
  const [loading, setLoading] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };
  const genres = ["Blues", "R&B", "Jazz", "Gospel", "Rock", "Soft Rock", "Neo Soul", "Techno"];

  const handleUpdate = async () => {
    setLoading(true);
    try {
      await axios.put(`${API}/admin/now-playing`, formData, { headers });
      toast.success("Now Playing updated!");
    } catch (e) {
      toast.error("Failed to update");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="grid md:grid-cols-2 gap-3">
        <input placeholder="Track Title" value={formData.track_title} onChange={(e) => setFormData({...formData, track_title: e.target.value})} className="radio-input text-sm" />
        <input placeholder="Artist" value={formData.artist} onChange={(e) => setFormData({...formData, artist: e.target.value})} className="radio-input text-sm" />
        <input placeholder="Album" value={formData.album} onChange={(e) => setFormData({...formData, album: e.target.value})} className="radio-input text-sm" />
        <select value={formData.genre} onChange={(e) => setFormData({...formData, genre: e.target.value})} className="radio-input text-sm">
          {genres.map(g => <option key={g} value={g}>{g}</option>)}
        </select>
        <input type="number" placeholder="Duration (seconds)" value={formData.duration} onChange={(e) => setFormData({...formData, duration: parseInt(e.target.value)})} className="radio-input text-sm" />
        <input placeholder="Artwork URL" value={formData.artwork_url} onChange={(e) => setFormData({...formData, artwork_url: e.target.value})} className="radio-input text-sm" />
      </div>
      <button onClick={handleUpdate} disabled={loading} className="chrome-btn text-xs flex items-center gap-2">
        <Radio className="w-4 h-4" /> {loading ? "Updating..." : "Update Now Playing"}
      </button>
    </div>
  );
};

// Newsletter Manager
const NewsletterManager = ({ token }) => {
  const [subscribers, setSubscribers] = useState([]);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { fetchSubscribers(); }, []);

  const fetchSubscribers = async () => {
    try {
      const res = await axios.get(`${API}/admin/newsletter`, { headers });
      setSubscribers(res.data);
    } catch (e) { console.error(e); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Remove this subscriber?")) return;
    try {
      await axios.delete(`${API}/admin/newsletter/${id}`, { headers });
      toast.success("Subscriber removed");
      fetchSubscribers();
    } catch (e) {
      toast.error("Failed to remove");
    }
  };

  return (
    <div>
      <p className="text-chrome-mid mb-3">{subscribers.length} subscribers</p>
      <div className="space-y-2 max-h-64 overflow-y-auto">
        {subscribers.map(sub => (
          <div key={sub.id} className="flex items-center justify-between p-2 bg-black/30 border border-white/5">
            <div>
              <p className="text-sm">{sub.email}</p>
              {sub.name && <p className="text-xs text-chrome-mid">{sub.name}</p>}
            </div>
            <button onClick={() => handleDelete(sub.id)} className="p-2 hover:bg-red-500/20 text-red-500"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
};

// Admin Avatar Dropdown with Credentials Update
const AdminAvatarDropdown = ({ token, username, onLogout, onTokenUpdate }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showCredentialsModal, setShowCredentialsModal] = useState(false);
  const [formData, setFormData] = useState({
    current_password: "",
    new_username: "",
    new_password: "",
    confirm_password: ""
  });
  const [loading, setLoading] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const handleUpdateCredentials = async (e) => {
    e.preventDefault();
    
    if (formData.new_password && formData.new_password !== formData.confirm_password) {
      toast.error("New passwords do not match");
      return;
    }

    if (!formData.current_password) {
      toast.error("Current password is required");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        current_password: formData.current_password
      };
      if (formData.new_username) payload.new_username = formData.new_username;
      if (formData.new_password) payload.new_password = formData.new_password;

      const res = await axios.put(`${API}/admin/credentials`, payload, { headers });
      
      // Update token if returned
      if (res.data.access_token) {
        localStorage.setItem("hotlive95_admin_token", res.data.access_token);
        onTokenUpdate(res.data.access_token);
      }
      
      toast.success("Credentials updated successfully!");
      setShowCredentialsModal(false);
      setFormData({ current_password: "", new_username: "", new_password: "", confirm_password: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update credentials");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative">
      {/* Avatar Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 p-2 hover:bg-white/10 rounded transition-colors"
        data-testid="admin-avatar-btn"
      >
        <div className="w-10 h-10 bg-neon-red rounded-full flex items-center justify-center">
          <User className="w-5 h-5 text-white" />
        </div>
        <div className="hidden md:block text-left">
          <p className="text-sm font-medium">{username}</p>
          <p className="text-xs text-chrome-mid">Administrator</p>
        </div>
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="absolute right-0 top-full mt-2 w-64 bg-surface border border-white/10 shadow-xl z-50"
        >
          <div className="p-4 border-b border-white/10">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-neon-red rounded-full flex items-center justify-center">
                <User className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="font-medium">{username}</p>
                <p className="text-xs text-chrome-mid">Admin Account</p>
              </div>
            </div>
          </div>
          
          <div className="p-2">
            <button
              onClick={() => { setShowCredentialsModal(true); setIsOpen(false); }}
              className="w-full flex items-center gap-3 p-3 hover:bg-white/5 transition-colors text-left"
              data-testid="change-credentials-btn"
            >
              <Key className="w-4 h-4 text-chrome-mid" />
              <div>
                <p className="text-sm">Change Credentials</p>
                <p className="text-xs text-chrome-mid">Update username & password</p>
              </div>
            </button>
            
            <button
              onClick={() => { setShowCredentialsModal(true); setIsOpen(false); }}
              className="w-full flex items-center gap-3 p-3 hover:bg-white/5 transition-colors text-left"
            >
              <Settings className="w-4 h-4 text-chrome-mid" />
              <div>
                <p className="text-sm">Account Settings</p>
                <p className="text-xs text-chrome-mid">Manage your account</p>
              </div>
            </button>
            
            <div className="border-t border-white/10 mt-2 pt-2">
              <button
                onClick={onLogout}
                className="w-full flex items-center gap-3 p-3 hover:bg-red-500/10 transition-colors text-left text-red-500"
                data-testid="dropdown-logout-btn"
              >
                <LogOut className="w-4 h-4" />
                <span className="text-sm">Sign Out</span>
              </button>
            </div>
          </div>
        </motion.div>
      )}

      {/* Credentials Update Modal */}
      {showCredentialsModal && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[100] flex items-center justify-center p-4"
          onClick={() => setShowCredentialsModal(false)}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-surface border border-white/10 p-6 max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
            data-testid="credentials-modal"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-neon-red flex items-center justify-center">
                <Key className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="font-display text-xl">UPDATE CREDENTIALS</h2>
                <p className="text-xs text-chrome-mid">Change your admin login details</p>
              </div>
            </div>

            <form onSubmit={handleUpdateCredentials} className="space-y-4">
              <PasswordInput
                value={formData.current_password}
                onChange={(e) => setFormData({...formData, current_password: e.target.value})}
                placeholder="Enter current password"
                testId="current-password-input"
                label="CURRENT PASSWORD *"
              />

              <div className="border-t border-white/10 pt-4">
                <p className="text-xs text-chrome-mid mb-3">Leave blank to keep current value</p>
                
                <div className="space-y-4">
                  <div>
                    <label className="font-mono text-xs tracking-widest text-chrome-mid block mb-2">
                      NEW USERNAME
                    </label>
                    <input
                      type="text"
                      value={formData.new_username}
                      onChange={(e) => setFormData({...formData, new_username: e.target.value})}
                      className="radio-input"
                      placeholder="Enter new username"
                      data-testid="new-username-input"
                    />
                  </div>

                  <PasswordInput
                    value={formData.new_password}
                    onChange={(e) => setFormData({...formData, new_password: e.target.value})}
                    placeholder="Enter new password"
                    testId="new-password-input"
                    label="NEW PASSWORD"
                    required={false}
                  />

                  <PasswordInput
                    value={formData.confirm_password}
                    onChange={(e) => setFormData({...formData, confirm_password: e.target.value})}
                    placeholder="Confirm new password"
                    testId="confirm-password-input"
                    label="CONFIRM NEW PASSWORD"
                    required={false}
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <button 
                  type="submit" 
                  className="chrome-btn flex-1"
                  disabled={loading}
                  data-testid="save-credentials-btn"
                >
                  {loading ? "Updating..." : "Update Credentials"}
                </button>
                <button 
                  type="button"
                  onClick={() => setShowCredentialsModal(false)}
                  className="outline-btn"
                >
                  Cancel
                </button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};

// Main Admin Dashboard
const AdminDashboard = ({ token, onLogout, onTokenUpdate }) => {
  const [stats, setStats] = useState(null);
  const [username, setUsername] = useState("admin");

  useEffect(() => { 
    fetchStats(); 
    fetchUsername();
  }, []);

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/admin/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(res.data);
    } catch (e) { console.error(e); }
  };

  const fetchUsername = async () => {
    try {
      const res = await axios.get(`${API}/admin/current-user`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsername(res.data.username);
    } catch (e) { console.error(e); }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header with Navigation */}
      <div className="bg-surface border-b border-white/10 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          {/* Left - Logo & Title */}
          <div className="flex items-center gap-6">
            <a href="/" className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_soul-station/artifacts/r4wkew7h_Untitled%20design.png" 
                alt="Hot Live 95 Detroit"
                className="h-12 w-auto"
              />
            </a>
            <div className="hidden md:block">
              <h1 className="font-display text-lg text-neon-red">ADMIN DASHBOARD</h1>
              <p className="font-mono text-[10px] text-chrome-mid">ADMINISTRATIVE USE ONLY</p>
            </div>
          </div>

          {/* Center - Navigation Links */}
          <nav className="hidden lg:flex items-center gap-6">
            <a href="/" className="font-body text-sm uppercase tracking-widest text-chrome-mid hover:text-white transition-colors">Home</a>
            <a href="/segments" className="font-body text-sm uppercase tracking-widest text-chrome-mid hover:text-white transition-colors">Segments</a>
            <a href="/schedule" className="font-body text-sm uppercase tracking-widest text-chrome-mid hover:text-white transition-colors">Schedule</a>
            <a href="/request" className="font-body text-sm uppercase tracking-widest text-chrome-mid hover:text-white transition-colors">Request</a>
            <a href="/submit" className="font-body text-sm uppercase tracking-widest text-neon-red hover:text-white transition-colors">Submit</a>
          </nav>
          
          {/* Right - Avatar Dropdown */}
          <AdminAvatarDropdown 
            token={token} 
            username={username} 
            onLogout={onLogout}
            onTokenUpdate={(newToken) => {
              onTokenUpdate(newToken);
              fetchUsername();
            }}
          />
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
            <StatsCard icon={Users} label="USERS" value={stats.users} />
            <StatsCard icon={Music} label="SEGMENTS" value={stats.segments} />
            <StatsCard icon={Send} label="REQUESTS" value={stats.song_requests.total} subValue={`${stats.song_requests.pending} pending`} />
            <StatsCard icon={Upload} label="SUBMISSIONS" value={stats.submissions.total} subValue={`${stats.submissions.pending} pending / ${stats.submissions.approved || 0} approved`} />
            <StatsCard icon={RotateCw} label="ROTATION" value={stats.rotation?.total || 0} subValue={`${stats.rotation?.scheduled || 0} scheduled`} />
            <StatsCard icon={Mic2} label="DJS" value={stats.dj_profiles || 0} />
          </div>
        )}

        {/* Management Sections */}
        <CollapsibleSection title="Segments" icon={Music} defaultOpen>
          <SegmentsManager token={token} />
        </CollapsibleSection>

        <CollapsibleSection title="Weekly Schedule" icon={Calendar}>
          <ScheduleManager token={token} />
        </CollapsibleSection>

        <CollapsibleSection title="Song Requests" icon={Send}>
          <RequestsManager token={token} />
        </CollapsibleSection>

        <CollapsibleSection title="Song & Video Submissions" icon={Upload} defaultOpen>
          <SubmissionsManager token={token} />
        </CollapsibleSection>

        <CollapsibleSection title="Rotation Schedule" icon={RotateCw}>
          <RotationManager token={token} />
        </CollapsibleSection>

        <CollapsibleSection title="DJ Profiles" icon={Mic2}>
          <DJManager token={token} />
        </CollapsibleSection>

        <CollapsibleSection title="Now Playing" icon={Radio}>
          <NowPlayingManager token={token} />
        </CollapsibleSection>

        <CollapsibleSection title="Live Podcast Stream" icon={Tv}>
          <PodcastStreamManager token={token} />
        </CollapsibleSection>

        <CollapsibleSection title="Newsletter Subscribers" icon={Mail}>
          <NewsletterManager token={token} />
        </CollapsibleSection>
      </div>
    </div>
  );
};

// Main Admin Page Export
export default function AdminPage() {
  const { isAdmin, adminToken, loading, login, logout, setToken } = useAdminAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-chrome-mid">Loading...</div>
      </div>
    );
  }

  if (!isAdmin) {
    return <AdminLogin onLogin={login} />;
  }

  return <AdminDashboard token={adminToken} onLogout={logout} onTokenUpdate={setToken} />;
}
